<script setup lang="ts">
import { timeFrom } from '@qctsw/utils'
import { imageTransform } from '@qctsw/common'
import { pull } from 'lodash-es'
import type {
  BlurHandleParams,
  CommentResponse,
  CommonType,
} from '@/server/types'
import type { CommentFavorHandleParams } from '@/composables/useComment'

const props = withDefaults(defineProps<{
  dataType?: CommonType
  dataId?: string | number
  list?: CommentResponse[] | CommentResponse['replyLs']
  total?: number
  hasMore?: boolean
  isLoading?: boolean
  isChild?: boolean
  firstLevelIndex?: number
}>(), {
  dataType: 'complaint',
  dataId: '-1',
  list: () => [],
  total: 0,
  hasMore: true,
  isLoading: false,
  isChild: false,
  firstLevelIndex: -1,
})
const emits = defineEmits({
  getCommentList: () => true,
})

const commentActiveList = reactive<string[]>([])
// 输入框失去焦点
function commentBlurHandle(data: BlurHandleParams) {
  setTimeout(() => {
    const key = `${data.firstLevelIndex}/${data.secondLevelIndex ?? -1}`
    if (commentActiveList.includes(key) && !data.content)
      pull(commentActiveList, key)
  }, 200)
}
function commentReplyHandle(data: CommentFavorHandleParams, isShow: boolean) {
  const key = `${data.firstLevelIndex}/${data.secondLevelIndex ?? -1}`
  if (!isShow)
    commentActiveList.push(key)
  else
    pull(commentActiveList, key)
}
function checkReplyInputShow(index: number) {
  const key = props.isChild ? `${props.firstLevelIndex}/${index}` : `${index}/-1`
  return commentActiveList.includes(key)
}

// 评论点赞
const commentFavorHandle = useDebounceFn((data: CommentFavorHandleParams) => {
  useCommentFavorite().favorite({
    dataType: props.dataType,
    commentId: data.item.id,
    isFavorite: !data.item.isGood,
    firstLevelIndex: data.firstLevelIndex,
    secondLevelIndex: data.secondLevelIndex,
  })
}, 200)

// 所有评论的ref相关
let itemRefs: any[] = []
function setItemRef(el: any) {
  if (el)
    itemRefs.push(el)
}
onBeforeUpdate(() => {
  itemRefs = []
})

function getAvatar(item: CommentResponse | CommentResponse['replyLs'][number]) {
  if (props.isChild)
    return (item as CommentResponse['replyLs'][number]).headImg
  return (item as CommentResponse).headImg
}

function getIndex(index: number, level: 'first' | 'second') {
  // 获取一级评论的索引
  if (level === 'first')
    return !props.isChild ? index : props.firstLevelIndex
  // 获取二级评论的索引
  else if (level === 'second')
    return props.isChild ? index : -1

  return -1
}
</script>

<template>
  <div>
    <template v-if="list.length">
      <div v-for="(v, i) in list" :key="i" class="pt-sm flex text-3.5 border-b-1 border-slate-1 last:border-b-0">
        <NAvatar v-if="getAvatar(v)" :size="46" round class="mr-sm flex-shrink-0" color="#2080f0" :src="imageTransform(getAvatar(v), { width: 100, height: 100 })" />
        <div v-else class="flex items-center justify-center w-11.5 h-11.5 rounded-full bg-blue-5 text-white text-5 font-bold mr-sm">
          {{ v?.userName ? v?.userName?.slice(0, 1) : "无" }}
        </div>
        <div class="flex-grow min-w-0 flex-1">
          <div class="font-bold">
            {{ v.userName ? v.userName : "用户暂未设置用户名" }}
          </div>
          <div class="text-slate-6 pt-2 pb-2 text-4">
            <span v-if="props.isChild">
              <span class="text-3.5 mr-1.25">回复</span>
              <span class="font-bold text-3.5 text-slate-8">{{ (v as CommentResponse['replyLs'][number]).toUserName }}</span>:
            </span>
            <p class="text-justify break-words">
              {{ v.content }}
            </p>
          </div>
          <div :ref="setItemRef" class="flex items-end justify-between text-3 text-slate-4 pb-sm">
            <div>{{ timeFrom(v.createTime, "YYYY-MM-DD HH:mm") }}</div>
            <div class="flex items-center">
              <div
                class="cursor-pointer ml-xl"
                :class="{ 'text-blue-5': checkReplyInputShow(i) }"
                @click=" commentReplyHandle({
                  item: v,
                  isChild: props.isChild,
                  firstLevelIndex: getIndex(i, 'first'),
                  secondLevelIndex: getIndex(i, 'second'),
                }, checkReplyInputShow(i))"
              >
                <i class="i-uil:comment-dots text-4 mr-0.75" />
              </div>
              <div
                class="cursor-pointer ml-xl"
                :class="{ 'text-blue-5': v.isGood }"
                @click="commentFavorHandle({
                  item: v,
                  isChild: props.isChild,
                  firstLevelIndex: getIndex(i, 'first'),
                  secondLevelIndex: getIndex(i, 'second'),
                })"
              >
                <i class="text-4 mr-0.75 vertical-icon" :class="[v.isGood ? 'i-bxs:like' : 'i-bx:like']" />
                <span v-if="(v as CommentResponse).goodCount || (v as CommentResponse['replyLs'][number]).commentGoodCount">
                  {{ (v as CommentResponse).goodCount || (v as CommentResponse['replyLs'][number]).commentGoodCount }}
                </span>
              </div>
            </div>
          </div>
          <div v-if="checkReplyInputShow(i)" class="pb-sm">
            <BasicCommentReplyInput
              :data-type="props.dataType"
              :data-id="props.dataId"
              :comment-id="v.id"
              :reply-name="v.userName"
              :first-level-index="getIndex(i, 'first')"
              :second-level-index="getIndex(i, 'second')"
              @blur-handle="commentBlurHandle"
            />
          </div>
          <!-- reply -->
          <div v-if="!props.isChild && (v as CommentResponse).replyLs && (v as CommentResponse).replyLs.length" class="border-t-1 border-slate-1">
            <LayoutCommentList
              :list="(v as CommentResponse).replyLs"
              :data-id="props.dataId"
              :data-type="props.dataType"
              :is-child="true"
              :first-level-index="i"
            />
          </div>
        </div>
      </div>
    </template>
    <BasicEmpty
      v-if="!props.isChild && !list.length"
      class="pb-xl"
      icon-class="i-custom-undraw-annotation?bg w-full text-30 m-xl"
      text="暂无评论，留下您的评论吧"
    />
    <template v-if="!props.isChild && list.length">
      <div
        v-if="hasMore"
        class="cursor-pointer flex items-center justify-center text-blue-5 mt-sm"
        @click="emits('getCommentList')"
      >
        <i v-if="isLoading" class="i-eos-icons:loading mr-0.5 text-0.9em vertical-icon" />
        <span class="text-4 tracking-0.5">查看更多</span>
      </div>
      <div v-else class="text-slate-4 text-center cursor-not-allowed mt-sm">
        没有更多了
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>

</style>
